import type { TFunction } from 'i18next'
import { Trash2 } from 'lucide-react'

import { UserDisplay } from '@/components'
import { BookmarkIndicator } from '@/components/BookmarkIndicator'
import { TableColumn } from '@/components/Table'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { canDeleteFiles } from '@/features/deliverable-set'
import { canDeleteDocument } from '@/features/view-tfl-uploaded/utils/validation.ts'
import { ResponseDeliverableSet, ResponseDocumentListItem } from '@/schema'
import { formatDisplayDate } from '@/utils'

import { Alignment, FreezePosition } from '../../components/Table/tableHelper'

import { DocumentStatus } from './components/DocumentStatus'

export type DocumentListAction = 'delete' | 'view'

export const getDocumentListColumns = (
  handleActionClick: (
    action: DocumentListAction,
    item: ResponseDocumentListItem
  ) => void,
  t: TFunction,
  deliverableSet: ResponseDeliverableSet | null = null
): TableColumn<ResponseDocumentListItem>[] => {
  const canDeleteByDsStatus = deliverableSet
    ? canDeleteFiles(deliverableSet)
    : true

  const columns: TableColumn<ResponseDocumentListItem>[] = [
    {
      id: 'display_name',
      label: t('document.columns.name'),
      width: 300,
      sortable: false,
      renderCell: (value, row) => {
        const displayValue =
          typeof value === 'string' || typeof value === 'number' ? value : ''
        const isBookmarked = !!row.is_bookmarked

        return (
          <div className="flex items-center gap-2">
            <span className="font-medium truncate">{displayValue}</span>
            {isBookmarked && (
              <BookmarkIndicator className="h-4 w-4 text-foreground-fg-quaternary-400 flex-shrink-0" />
            )}
          </div>
        )
      },
    },
    {
      id: 'status',
      label: t('document.columns.status'),
      width: 150,
      sortable: true,
      renderCell: (value) => {
        const status = value as string
        return status ? <DocumentStatus status={status} /> : '-'
      },
      align: Alignment.RIGHT,
      alignLabel: Alignment.RIGHT,
    },
    {
      id: 'snapshot_comment_open',
      label: t('document.columns.openComments'),
      width: 180,
      renderCell: (value) => {
        const count = typeof value === 'number' ? value : 0
        return <span>{count}</span>
      },
      align: Alignment.RIGHT,
      alignLabel: Alignment.RIGHT,
    },
    {
      id: 'created_by_user',
      label: t('document.columns.createdBy'),
      width: 250,
      sortable: true,
      renderCell: (_value, row) => {
        return <UserDisplay user={row.created_by_user} showAvatar={false} />
      },
    },
    {
      id: 'created_at',
      label: t('document.columns.createdDate'),
      width: 150,
      sortable: true,
      align: Alignment.LEFT,
      alignLabel: Alignment.LEFT,
      renderCell: (value) => <span>{formatDisplayDate(value as string)}</span>,
    },
  ]

  if (canDeleteByDsStatus) {
    columns.push({
      id: 'actions',
      width: 90,
      isFrozen: true,
      freezePosition: FreezePosition.RIGHT,
      renderCell: (_value, row) => {
        const canDeleteByDocumentStatus = canDeleteDocument(row.status)
        const showDelete = canDeleteByDocumentStatus

        return (
          <div className="flex gap-0.5 justify-center items-center">
            {showDelete && (
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <Button
                    variant="text"
                    className="border-none bg-transparent p-1.5 w-fit h-fit text-foreground-fg-quaternary-400 hover:bg-transparent"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleActionClick('delete', row)
                    }}
                  >
                    <Trash2 size={16} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <span>{t('document.actions.delete')}</span>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        )
      },
    })
  }

  return columns
}
